import React, { useCallback, useEffect } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { SmartApplyTemplate } from "../../../template/smart-apply/default-template";
import { Loader } from "../../../organisms/common/loading";
import { DetailPage } from "../../../organisms/smart-apply/procedure-detail/basic-info";
import { FormEditPage } from "../../../organisms/smart-apply/procedure-detail/form-edit/form-edit";
import { AuthLevel } from "../../../organisms/smart-apply/procedure-detail/auth-level";
import {
  ProcedureContextProps,
  ProcedureDetailService,
} from "../../../../application/service/smart-apply/procedure-detail/procedure-detail-service";
import { TabState } from "../../../../application/service/smart-apply/procedure-detail/tab-state";
import { BaseModal } from "../../../atoms/base-modal";
import { useModal } from "../../../../utils/custom-hooks";
import { PermissionErrorMessages } from "../../../organisms/common/permission-error-messages";
import {
  PORTAL_BASE_URL,
  parseQueryOfLocation,
} from "../../../../utils/url-utils";
import { PresentationSettingContainer } from "../../../organisms/smart-apply/procedure-detail/presentation-setting";
import { MailCfgContainer } from "../../../organisms/smart-apply/procedure-detail/mail-cfg";
import { QuotaSettingContainer } from "../../../organisms/smart-apply/procedure-detail/quota-setting";
import { PublishSettingModal } from "../../../organisms/common/publish-setting-modal";
import { SmartApplyR } from "../route";
import { GroupShareModal } from "../../../organisms/common/group-share-modal";
import { ConfirmModal } from "../../../organisms/common/confirm-modal";
import {
  connectPageService,
  envConfig,
  isLGWANURL,
} from "@graffer-inc/smart-apply";
import { PaymentSetting } from "../../../organisms/smart-apply/procedure-detail/payment-setting";
import { useAuthContextV2 } from "../../../../auth-state";
import { ConfirmMatrixCodeModal } from "../../../organisms/smart-apply/modals/confirm-matrix-code-modal";
import { ProcedureCopyModal } from "../../../organisms/common/procedure-copy-modal";
import { TenantProcessResultSettingPage } from "../../../organisms/smart-apply/procedure-detail/tenant-process-result-setting/index";
import { PdfOutputConfigurationList } from "../../../organisms/smart-apply/procedure-detail/pdf-output-configuration/list";
import { AssignCustomPathModalContainer } from "../../../organisms/smart-apply/modals/assign-custom-path";
import {
  InformationBanner,
  SecondaryMenuList,
} from "@graffer-inc/console-design-kit";
import { useRBAC } from "../../../helpers/rbac/use-rbac";
import { useFormUnsaved } from "../../../../utils/use-form-unsaved";
import { ProcedureDetailHeader } from "./contentsHeader";
import apiClient from "../../../../application/apis/api-client";
import { ConfirmArchiveModal } from "../../../organisms/smart-apply/modals/confirm-archive-modal";

const ProcedureDetailPage: React.FC<ProcedureContextProps> = ({
  isSubmitting,
  resourceLoadState,
  procedure,
  customPathSettings,
  hasPresentation,
  hasCustomUIDoorPage,
  tabState,
  switchTab,
  onProcedureUpdate,
  onCopyCreate,
  onReturnToDraft,
  onProcedureDelete,
  onUnPublish,
  onPauseAccepting,
  onResumeAccepting,
  resetState,
  onGroupAdd,
  onDraftUpdate,
  onPublishSettingUpdate,
  onExportProcedure,
  onArchive,
  orgSlug,
  procMsts,
  onUpdateProcMaster,
  setAuthCtx,
  fetchProcMstBySmartApply,
  onUnarchive,
}) => {
  const { auth } = useAuthContextV2();
  const { id } = useParams<{ id: string }>();
  const { tab } = parseQueryOfLocation(useLocation()) as {
    tab: string | undefined;
  };
  const history = useHistory();
  const shareModal = useModal();
  const publishSettingModal = useModal();
  const unpublishConfirmModal = useModal();
  const returnToDraftModal = useModal();
  const pauseAcceptModal = useModal();
  const resumeAcceptModal = useModal();
  const deleteConfirmModal = useModal();
  const copyProcedureModal = useModal();
  const assignCustomPathModal = useModal();
  const exportProcedureModal = useModal();
  const matrixCodeModal = useModal();
  const archiveModal = useModal();
  const { setIsFormUnsaved, confirmWhenFormUnsaved, handleSwitchTab } =
    useFormUnsaved({
      tabState,
      switchTab,
      product: "SMART_APPLY",
      object: procedure,
    });

  useEffect(() => {
    setAuthCtx(auth.data);
    resetState(id, tab);
  }, [resetState, setAuthCtx, id, tab, auth]);

  const useProcMst = auth.isProcedureMasterAvailable();
  useEffect(() => {
    if (useProcMst && procedure) {
      fetchProcMstBySmartApply(procedure.id, procedure.orgId);
    }
  }, [fetchProcMstBySmartApply, useProcMst, procedure]);

  const onCopy = async (newName: string) => {
    try {
      const newProc = await onCopyCreate(newName);
      history.push(SmartApplyR.ProcedureDetail.link({ id: newProc.id }));
    } catch (e) {
      throw e;
    }
  };

  // 二次元コード生成画面遷移処理
  const openMatrixCodeWindow = useCallback(() => {
    if (procedure) {
      window.open(
        `/graffer-utility-tools/matrix-code?queryUrl=${encodeURIComponent(
          procedure.doorPageURL(orgSlug, customPathSettings.currentPath),
        )}`,
        "_blank",
      );
    }
  }, [orgSlug, procedure, customPathSettings.currentPath]);

  const checkPermission = useRBAC();

  const handleOnGoToParentPage = useCallback(() => {
    // アーカイブされている手続きは、編集ができないので、保存されていないかは確認せずに画面遷移する
    if (procedure && procedure.isArchived()) {
      history.push(SmartApplyR.ArchivedProcedures.link({}));
      return;
    }

    confirmWhenFormUnsaved(() => {
      history.push(SmartApplyR.Procedures.link({}));
    });
  }, [confirmWhenFormUnsaved, history, procedure]);

  const canShowPreview: boolean =
    !!procedure && !procedure.isFormAccessRestricted();
  const openPreview = async () => {
    const { data } = await apiClient.generateProcedurePreviewToken(
      procedure!.id,
    );
    const host = isLGWANURL(window.location.href)
      ? envConfig.getLgwanHost()
      : PORTAL_BASE_URL!;
    const previewLink = procedure!.previewDoorURL(
      host,
      orgSlug,
      data.previewToken,
    );

    window.open(previewLink, "_blank");
  };

  return (
    <SmartApplyTemplate
      breadcrumbs={[
        { to: SmartApplyR.Procedures.link({}), title: "手続き一覧" },
        { to: SmartApplyR.ProcedureDetail.link({ id: id }), title: id },
      ]}
      contentsHeader={
        <ProcedureDetailHeader
          {...{
            id,
            isAdmin: auth.hasRole("Admin"),
            orgSlug,
            currentCustomPath: customPathSettings.currentPath,
            resourceLoadState,
            procedure,
            onCopyOpen: copyProcedureModal.open,
            onDeleteOpen: deleteConfirmModal.open,
            onManageGroupOpen: shareModal.open,
            onPublishSettingModalOpen: publishSettingModal.open,
            onUnPublish: unpublishConfirmModal.open,
            onPauseAccepting: pauseAcceptModal.open,
            onCustomPathOpen: assignCustomPathModal.open,
            onExportProcedure: exportProcedureModal.open,
            onMatrixCodeOpen: customPathSettings.currentPath
              ? openMatrixCodeWindow
              : matrixCodeModal.open,
            onReturnToDraft: returnToDraftModal.open,
            onResumeAccepting: resumeAcceptModal.open,
            canShowPreview: canShowPreview,
            onOpenPreview: openPreview,
            onUnarchive: onUnarchive,
            onArchiveOpen: archiveModal.open,
          }}
        />
      }
      menuList={
        <SecondaryMenuList
          parent={{
            title:
              procedure && procedure.isArchived()
                ? "アーカイブ一覧"
                : "手続き一覧",
            anchor: ({ children, ...props }) => (
              <button onClick={handleOnGoToParentPage} {...props}>
                {children}
              </button>
            ),
          }}
          items={[
            {
              title: "基本情報",
              active: tabState.equals("BASIC"),
              anchor: ({ children, ...props }) => (
                <button
                  onClick={() => handleSwitchTab(TabState.initialize())}
                  {...props}
                >
                  {children}
                </button>
              ),
              hidden: false,
            },
            {
              title: "申請フォーム",
              active: tabState.equals("FORM"),
              anchor: ({ children, ...props }) => (
                <button
                  onClick={() => handleSwitchTab(TabState.form())}
                  {...props}
                >
                  {children}
                </button>
              ),
              hidden: !checkPermission({
                target: procedure,
                requirePermissions: "procedures.read",
              }),
            },
            {
              title: "認証",
              active: tabState.equals("AUTH_LEVEL"),
              anchor: ({ children, ...props }) => (
                <button
                  onClick={() => handleSwitchTab(TabState.authLevel())}
                  {...props}
                >
                  {children}
                </button>
              ),
              hidden: !checkPermission({
                target: procedure,
                requirePermissions: "procedures.read",
              }),
            },
            {
              title: "決済",
              active: tabState.equals("PAYMENT"),
              anchor: ({ children, ...props }) => (
                <button
                  onClick={() => handleSwitchTab(TabState.payment())}
                  {...props}
                >
                  {children}
                </button>
              ),
              hidden:
                !auth.usePayment ||
                !checkPermission({
                  target: procedure,
                }),
            },
            {
              title: "通知",
              active: tabState.equals("NOTIFICATION_MAIL"),
              anchor: ({ children, ...props }) => (
                <button
                  onClick={() => handleSwitchTab(TabState.notificationMail())}
                  {...props}
                >
                  {children}
                </button>
              ),
              hidden: !checkPermission({
                target: procedure,
                requirePermissions: "procedures.mail_cfg_read",
              }),
            },
            {
              title: "申請数の上限",
              active: tabState.equals("QUOTA"),
              anchor: ({ children, ...props }) => (
                <button
                  onClick={() => handleSwitchTab(TabState.quota())}
                  {...props}
                >
                  {children}
                </button>
              ),
              hidden: !checkPermission({
                target: procedure,
                requirePermissions: "procedures.read",
              }),
            },
            {
              title: "事務処理の設定",
              active: tabState.equals("INTERNAL_WORK"),
              anchor: ({ children, ...props }) => (
                <button
                  onClick={() => handleSwitchTab(TabState.internalWork())}
                  {...props}
                >
                  {children}
                </button>
              ),
              hidden: !checkPermission({
                requirePermissions: "procedures.read",
              }),
            },
            {
              title: "帳票レイアウト",
              active: tabState.equals("REPORT_OUTPUT"),
              anchor: ({ children, ...props }) => (
                <button
                  onClick={() => handleSwitchTab(TabState.reportOutput())}
                  {...props}
                >
                  {children}
                </button>
              ),
              hidden: !checkPermission({
                requirePermissions: "procedures.pdf_output_setting_update",
              }),
            },
            {
              title: "カスタムページ",
              active: tabState.equals("CUSTOM_UI"),
              anchor: ({ children, ...props }) => (
                <button
                  onClick={() => handleSwitchTab(TabState.customUi())}
                  {...props}
                >
                  {children}
                </button>
              ),
              hidden: !(
                hasPresentation &&
                checkPermission({
                  target: procedure,
                  requirePermissions: "procedures.presentation_read",
                })
              ),
            },
          ]}
        />
      }
    >
      {resourceLoadState.matchType({
        LOADING: null,
        LOAD_FAILED: null,
        NO_PERMISSION: (
          <>
            <div className="columns is-vcentered">
              <div className="column has-text-left is-6">
                <PermissionErrorMessages
                  message={"この手続きの詳細を表示する権限がありません"}
                />
              </div>
            </div>
          </>
        ),
        HAS_PERMISSION: (
          <>
            {!!procedure && !!procedure.draft && (
              <>
                <Loader isLoading={isSubmitting}>
                  {tabState.matchType({
                    BASIC: (
                      <DetailPage
                        orgSlug={orgSlug}
                        procedure={procedure}
                        existingCustomPathSettings={customPathSettings}
                        onProcedureUpdate={onProcedureUpdate}
                        isServiceProviderAccount={false}
                        useProcMst={useProcMst}
                        procMsts={procMsts}
                        hasCustomUIDoorPage={hasCustomUIDoorPage}
                        onUpdateProcMaster={onUpdateProcMaster}
                      />
                    ),
                    FORM: (
                      <FormEditPage
                        procedure={procedure}
                        onDraftUpdate={onDraftUpdate}
                        setIsFormUnsaved={setIsFormUnsaved}
                      />
                    ),
                    AUTH_LEVEL: (
                      <AuthLevel
                        procedure={procedure}
                        onDraftUpdate={onDraftUpdate}
                      />
                    ),
                    PAYMENT: (
                      <PaymentSetting
                        procedure={procedure}
                        onDraftUpdate={onDraftUpdate}
                      />
                    ),
                    CUSTOM_UI: (
                      <PresentationSettingContainer procedure={procedure} />
                    ),
                    NOTIFICATION_MAIL: (
                      <MailCfgContainer procedure={procedure} />
                    ),
                    QUOTA: <QuotaSettingContainer procedure={procedure} />,
                    INTERNAL_WORK: (
                      <TenantProcessResultSettingPage
                        procedure={procedure}
                        onDraftUpdate={onDraftUpdate}
                      />
                    ),
                    REPORT_OUTPUT: (
                      <PdfOutputConfigurationList procedure={procedure} />
                    ),
                  })}
                </Loader>
                {shareModal.isActive && (
                  <GroupShareModal
                    onClose={shareModal.close}
                    onAdd={onGroupAdd}
                    sharedGroupIds={procedure.groupIds}
                  />
                )}
                <BaseModal
                  isActive={deleteConfirmModal.isActive}
                  title="手続きを削除"
                  onClose={deleteConfirmModal.close}
                >
                  <section className="modal-card-body">
                    <div className="is-small">
                      手続きを削除してよろしいでしょうか。
                    </div>
                  </section>
                  <footer className="modal-card-foot">
                    <button
                      className="button is-failure is-fullwidth"
                      onClick={deleteConfirmModal.close}
                    >
                      キャンセル
                    </button>
                    <button
                      className="button is-info is-fullwidth"
                      onClick={async () => {
                        await onProcedureDelete();
                        deleteConfirmModal.close();
                        history.push(SmartApplyR.Procedures.link({}));
                      }}
                    >
                      削除する
                    </button>
                  </footer>
                </BaseModal>
                <PublishSettingModal
                  publishSetting={procedure.publishSetting}
                  publishStatus={procedure.publishStatus}
                  isPublished={procedure.isPublished()}
                  isActive={publishSettingModal.isActive}
                  onPublishSettingUpdate={onPublishSettingUpdate}
                  onClose={publishSettingModal.close}
                />
              </>
            )}
          </>
        ),
      })}
      <BaseModal
        title={"手続きの公開終了"}
        isActive={unpublishConfirmModal.isActive}
        onClose={unpublishConfirmModal.close}
      >
        <section className="modal-card-body">
          <p>
            公開を終了すると申請を受けつけられなくなります。よろしいですか？
          </p>
        </section>
        <section className="modal-card-foot">
          <button
            type="button"
            className="button is-fullwidth"
            onClick={unpublishConfirmModal.close}
          >
            キャンセル
          </button>
          <button
            type="button"
            className="button is-fullwidth is-danger"
            onClick={() => {
              onUnPublish(procedure!.id);
              unpublishConfirmModal.close();
            }}
          >
            公開を終了する
          </button>
        </section>
      </BaseModal>
      <BaseModal
        title={"申請受付の一時停止"}
        isActive={pauseAcceptModal.isActive}
        onClose={pauseAcceptModal.close}
      >
        <section className="modal-card-body">
          <p>
            受付を一時停止すると再開するまで申請を受けつけることができなくなります。よろしいですか？
          </p>
        </section>
        <section className="modal-card-foot">
          <button
            type="button"
            className="button is-fullwidth"
            onClick={pauseAcceptModal.close}
          >
            キャンセル
          </button>
          <button
            type="button"
            className="button is-fullwidth is-danger"
            onClick={() => {
              onPauseAccepting(procedure!.id);
              pauseAcceptModal.close();
            }}
          >
            受付を一時停止する
          </button>
        </section>
      </BaseModal>
      <BaseModal
        title={"申請受付の再開"}
        isActive={resumeAcceptModal.isActive}
        onClose={resumeAcceptModal.close}
      >
        <section className="modal-card-body">
          <p>受付を再開します。よろしいですか？</p>
        </section>
        <section className="modal-card-foot">
          <button
            type="button"
            className="button is-fullwidth"
            onClick={resumeAcceptModal.close}
          >
            キャンセル
          </button>
          <button
            type="button"
            className="button is-fullwidth is-danger"
            onClick={() => {
              onResumeAccepting(procedure!.id);
              resumeAcceptModal.close();
            }}
          >
            受付を再開する
          </button>
        </section>
      </BaseModal>
      {procedure && copyProcedureModal.isActive && (
        <ProcedureCopyModal
          onCopy={onCopy}
          procedure={procedure}
          isActive={copyProcedureModal.isActive}
          onClose={copyProcedureModal.close}
        />
      )}
      <ConfirmModal
        isActive={returnToDraftModal.isActive}
        title="下書きに戻す"
        body="下書きに戻すとすべての項目の更新が可能になります。"
        successButtonText="戻す"
        onSuccess={() => {
          onReturnToDraft();
          returnToDraftModal.close();
        }}
        onClose={returnToDraftModal.close}
      />
      {assignCustomPathModal.isActive && (
        <AssignCustomPathModalContainer
          onClose={assignCustomPathModal.close}
          orgSlug={orgSlug}
          procedure={procedure!}
          afterReallocate={resetState}
        />
      )}
      <ConfirmModal
        isActive={exportProcedureModal.isActive}
        title="手続き情報をエクスポート"
        banner={
          procedure?.hasPdfOutputSettingForDraft() ? (
            <InformationBanner message={bannerLabelForProcedureExport} />
          ) : undefined
        }
        body="この手続きの設定をファイルとしてエクスポートします。"
        successButtonText="エクスポート"
        onSuccess={async () => {
          await onExportProcedure();
          exportProcedureModal.close();
        }}
        onClose={exportProcedureModal.close}
      />
      <ConfirmMatrixCodeModal
        isActive={matrixCodeModal.isActive}
        onCancel={matrixCodeModal.close}
        onBreak={() => {
          matrixCodeModal.close();
          assignCustomPathModal.open();
        }}
        onSubmit={() => {
          matrixCodeModal.close();
          openMatrixCodeWindow();
        }}
      />
      <ConfirmArchiveModal
        isActive={archiveModal.isActive}
        fromServiceProvider={false}
        onSubmit={async () => {
          await onArchive();
          archiveModal.close();
        }}
        onCancel={archiveModal.close}
      />
    </SmartApplyTemplate>
  );
};
const bannerLabelForProcedureExport = [
  "帳票レイアウトに設定されている帳票PDFは含まれません。",
  "エクスポートしたファイルから作成した手続きには、帳票PDFをアップロードしてください。",
].join("\n");

export const ProcedureDetailContainer = connectPageService(
  ProcedureDetailPage,
  () => new ProcedureDetailService(),
);
